import React, { useState, useRef, useEffect, useCallback } from 'react';
import Markdown from 'react-markdown';
import { TextField, SelectField, EmojiPickerField } from './ConfigFields';
import nodeLibrary from './nodeLibrary.json';

const { categories } = nodeLibrary;

// Config component for Prompt node (User Input)
function PromptConfig({ config, onChange }) {
  return (
    <div>
      <SelectField
        label="Input Location"
        value={config.inputLocation || 'terminal'}
        onChange={(v) => onChange({ ...config, inputLocation: v })}
        options={[
          { value: 'terminal', label: 'Terminal' }
        ]}
      />
    </div>
  );
}

// Config component for Response node (Agent Modification)
function RespondConfig({ config, onChange }) {
  return (
    <div>
      <SelectField
        label="Model"
        value={config.model || 'claude'}
        onChange={(v) => onChange({ ...config, model: v })}
        options={[
          { value: 'claude', label: 'Claude' }
        ]}
      />
    </div>
  );
}

// Convert a display title to a skill name (lowercase, dashes)
function toSkillName(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Update a specific field in YAML frontmatter within a template string
// If the field doesn't exist yet, adds it at the end of the frontmatter
function updateFrontmatter(template, field, value) {
  const match = template.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return template;
  const fm = match[1];
  const regex = new RegExp(`^${field}:.*$`, 'm');
  if (regex.test(fm)) {
    const newFm = fm.replace(regex, `${field}: ${value}`);
    return template.replace(/^---\n[\s\S]*?\n---/, `---\n${newFm}\n---`);
  }
  // Field doesn't exist — add it
  const newFm = fm + `\n${field}: ${value}`;
  return template.replace(/^---\n[\s\S]*?\n---/, `---\n${newFm}\n---`);
}

// Markdown content field with preview/edit toggle
function MarkdownField({ label, value, onChange, placeholder }) {
  const [editing, setEditing] = useState(false);
  const textareaRef = useRef(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, []);

  useEffect(() => {
    if (editing) autoResize();
  }, [editing, value, autoResize]);

  // Strip frontmatter for preview
  const bodyOnly = (value || '').replace(/^---\n[\s\S]*?\n---\n*/, '');

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>{label}</label>
      {editing ? (
        <textarea
          ref={textareaRef}
          autoFocus
          value={value || ''}
          onChange={(e) => {
            onChange(e.target.value);
            autoResize();
          }}
          placeholder={placeholder}
          rows={1}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: 'monospace',
            resize: 'none',
            overflow: 'hidden',
            outline: 'none'
          }}
          onBlur={() => setTimeout(() => setEditing(false), 100)}
        />
      ) : (
        <div
          onClick={() => setEditing(true)}
          style={{
            padding: '12px 14px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '13px',
            lineHeight: '1.6',
            cursor: 'pointer',
            minHeight: '80px',
            color: bodyOnly.trim() ? '#111827' : '#9ca3af'
          }}
        >
          {bodyOnly.trim() ? (
            <Markdown
              components={{
                h1: ({ children }) => <h1 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px', color: '#111827' }}>{children}</h1>,
                h2: ({ children }) => <h2 style={{ fontSize: '15px', fontWeight: 600, margin: '12px 0 6px', color: '#374151' }}>{children}</h2>,
                h3: ({ children }) => <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '10px 0 4px', color: '#374151' }}>{children}</h3>,
                p: ({ children }) => <p style={{ margin: '0 0 8px', color: '#4b5563' }}>{children}</p>,
                ul: ({ children }) => <ul style={{ margin: '0 0 8px', paddingLeft: '20px' }}>{children}</ul>,
                ol: ({ children }) => <ol style={{ margin: '0 0 8px', paddingLeft: '20px' }}>{children}</ol>,
                li: ({ children }) => <li style={{ marginBottom: '2px', color: '#4b5563' }}>{children}</li>,
                code: ({ children }) => <code style={{ fontSize: '12px', background: '#f3f4f6', padding: '1px 4px', borderRadius: '3px', fontFamily: 'monospace' }}>{children}</code>,
                pre: ({ children }) => <pre style={{ background: '#f3f4f6', padding: '10px 12px', borderRadius: '6px', fontSize: '12px', overflow: 'auto', margin: '0 0 8px' }}>{children}</pre>,
              }}
            >
              {bodyOnly}
            </Markdown>
          ) : (
            placeholder || 'Click to edit...'
          )}
        </div>
      )}
    </div>
  );
}

// Config component for template-based nodes (all new node types)
function TemplateConfig({ config, onChange, filepath }) {
  const handleTitleChange = (title) => {
    const skillName = toSkillName(title);
    let newTemplate = config.template || '';
    newTemplate = updateFrontmatter(newTemplate, 'name', skillName);
    onChange({ ...config, title, skillName, template: newTemplate });
  };

  const handleDescriptionChange = (desc) => {
    let newTemplate = config.template || '';
    newTemplate = updateFrontmatter(newTemplate, 'description', desc);
    onChange({ ...config, description: desc, template: newTemplate });
  };

  const handleIconChange = (icon) => {
    let newTemplate = config.template || '';
    newTemplate = updateFrontmatter(newTemplate, 'icon', icon);
    onChange({ ...config, icon, template: newTemplate });
  };

  const skillName = config.title ? toSkillName(config.title) : (config.skillName || '');

  return (
    <div>
      <SelectField
        label="Category"
        value={config.category || 'custom'}
        onChange={(v) => onChange({ ...config, category: v })}
        showDescriptions={true}
        options={categories.map(cat => ({
          value: cat.type,
          label: cat.label,
          description: cat.description
        }))}
      />
      <EmojiPickerField
        label="Icon"
        value={config.icon || ''}
        onChange={handleIconChange}
      />
      <TextField
        label="When to use"
        value={config.description || ''}
        onChange={handleDescriptionChange}
        placeholder="Brief description of this node..."
        multiline
        maxLength={150}
      />
      <MarkdownField
        label="Content"
        value={config.template}
        onChange={(v) => onChange({ ...config, template: v })}
        placeholder="Click to add content (markdown)..."
      />
      {filepath && (
        <div style={{
          fontSize: '12px',
          color: '#9ca3af',
          padding: '8px 10px',
          background: '#f9fafb',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          fontFamily: 'monospace',
          marginTop: '4px'
        }}>
          <span style={{ color: '#6b7280', fontWeight: 500 }}>File path: </span>{filepath}
        </div>
      )}
    </div>
  );
}

// Config component for Claude Init (start) node — just edits CLAUDE.md content
function ClaudeInitConfig({ config, onChange }) {
  return (
    <div>
      <MarkdownField
        label="Content"
        value={config.template}
        onChange={(v) => onChange({ ...config, template: v })}
        placeholder="Click to edit CLAUDE.md content..."
      />
      <div style={{
        fontSize: '12px',
        color: '#9ca3af',
        padding: '8px 10px',
        background: '#f9fafb',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        fontFamily: 'monospace',
        marginTop: '4px'
      }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>File path: </span>CLAUDE.md
      </div>
    </div>
  );
}

// Get config component for step type
export function getConfigComponent(nodeId) {
  switch (nodeId) {
    case 'start': return ClaudeInitConfig;
    case 'prompt': return PromptConfig;
    case 'response': return RespondConfig;
    default: return TemplateConfig;
  }
}
